import type { HarnessId } from "./harnessEventTypes"

export type ProviderUserMessagePurpose = "human" | "context" | "tool_result" | "unknown"
export type ProviderUserMessageKind = "message" | "steer"

export type ProviderContentPart = { type: "text"; text: string } | { type: "image"; uri: string; mediaType?: string } | { type: "unknown"; raw: unknown }

export interface ProviderUserMessage {
    purpose: ProviderUserMessagePurpose
    kind: ProviderUserMessageKind
    parts: ProviderContentPart[]
    timestamp?: string
    providerTurnId?: string
}

export function classifyProviderUserMessage(harnessId: HarnessId, raw: unknown): ProviderUserMessage | null {
    switch (harnessId) {
        case "codex":
            return classifyCodexUserMessage(raw)
        case "grok-build":
            return classifyGrokUserMessage(raw)
        case "opencode":
            return classifyOpenCodeUserMessage(raw)
        case "claude-code":
            return classifyClaudeUserMessage(raw)
    }
}

function classifyOpenCodeUserMessage(raw: unknown): ProviderUserMessage | null {
    const record = recordValue(raw)
    if (stringValue(record?.type)?.replace(/\.1$/, "") !== "message.part.updated") return null
    const properties = recordValue(record?.properties) ?? record
    const part = recordValue(properties?.part)
    if (!part) return null
    const info = recordValue(properties?.info)
    const role = stringValue(info?.role)
    if (role === "assistant" || (role !== "user" && recordValue(part.time))) return null
    const type = stringValue(part.type)
    const timestampValue = recordValue(part.time)?.start ?? recordValue(info?.time)?.created
    const timestamp = typeof timestampValue === "number" ? new Date(timestampValue).toISOString() : undefined
    if (type === "text") {
        const text = stringValue(part.text)?.trim()
        if (!text) return null
        return { purpose: "human", kind: "message", parts: [{ type: "text", text }], ...(timestamp ? { timestamp } : {}) }
    }
    if (type !== "file") return null
    const uri = stringValue(part.url)
    if (!uri) return null
    const mediaType = stringValue(part.mime)
    const content: ProviderContentPart = mediaType?.startsWith("image/") ? { type: "image", uri, mediaType } : { type: "unknown", raw: part }
    return { purpose: "human", kind: "message", parts: [content], ...(timestamp ? { timestamp } : {}) }
}

function classifyGrokUserMessage(raw: unknown): ProviderUserMessage | null {
    const record = recordValue(raw)
    const params = recordValue(record?.params)
    const update = recordValue(params?.update)
    if (stringValue(update?.sessionUpdate) !== "user_message_chunk") return null
    const content = recordValue(update?.content)
    const timestampValue = record?.timestamp
    const timestamp = typeof timestampValue === "number" ? new Date(timestampValue * 1000).toISOString() : stringValue(timestampValue)
    const parts = content ? contentParts([content]) : []
    if (parts.length === 0) return null
    return {
        purpose: "human",
        kind: "message",
        parts,
        ...(timestamp ? { timestamp } : {}),
    }
}

function classifyCodexUserMessage(raw: unknown): ProviderUserMessage | null {
    const record = recordValue(raw)
    if (!record) return null
    const type = stringValue(record.type)
    const payload = recordValue(record.payload)
    const timestamp = stringValue(record.timestamp)

    if (type === "event_msg" && payload && stringValue(payload.type) === "user_message") {
        return messageFromRecord(payload, "human", timestamp)
    }

    if (type === "response_item" && payload && stringValue(payload.type) === "message" && stringValue(payload.role) === "user") {
        const metadata = recordValue(payload.internal_chat_message_metadata_passthrough)
        const contentItemKinds = Array.isArray(metadata?.content_item_kinds)
            ? metadata.content_item_kinds.filter((value): value is string => typeof value === "string")
            : []
        const hasHumanProvenance = contentItemKinds.some((kind) => kind.startsWith("user."))
        const message = messageFromRecord(payload, hasHumanProvenance ? "human" : "context", timestamp, {
            omitCodexImageWrappers: true,
            providerTurnId: stringValue(metadata?.turn_id),
        })
        return message?.parts.some((part) => part.type === "image") ? { ...message, purpose: "human" } : message
    }

    if (type === "user_message") {
        return messageFromRecord(record, "human", timestamp)
    }

    if (type === "message" && stringValue(record.role) === "user") {
        return messageFromRecord(record, "context", timestamp)
    }

    return null
}

function classifyClaudeUserMessage(raw: unknown): ProviderUserMessage | null {
    const record = recordValue(raw)
    if (!record) return null
    const type = stringValue(record.type)
    const message = recordValue(record.message)
    const timestamp = stringValue(record.timestamp)
    const origin = recordValue(record.origin)

    // Claude delivers background-agent results as synthetic user records so the
    // parent model can resume. They are provider context, not a new human turn.
    if (type === "user" && stringValue(origin?.kind) === "task-notification") {
        return {
            purpose: "context",
            kind: "message",
            parts: [],
            ...(timestamp ? { timestamp } : {}),
        }
    }

    const attachment = recordValue(record.attachment)
    if (type === "attachment" && stringValue(attachment?.type) === "queued_command") {
        const parts = contentParts(attachment?.prompt)
        const queuedAt = stringValue(attachment?.timestamp) ?? timestamp
        return {
            purpose: "human",
            kind: "steer",
            parts: parts.length > 0 ? parts : [{ type: "unknown", raw: attachment?.prompt }],
            ...(queuedAt ? { timestamp: queuedAt } : {}),
        }
    }

    if (type === "user" && message) {
        return claudeMessageFromContent(message.content, timestamp)
    }

    if (type === "message" && stringValue(record.role) === "user") {
        return claudeMessageFromContent(record.content, timestamp)
    }

    return null
}

function claudeMessageFromContent(content: unknown, timestamp?: string): ProviderUserMessage {
    if (containsToolResult(content)) {
        return {
            purpose: "tool_result",
            kind: "message",
            parts: [{ type: "unknown", raw: content }],
            ...(timestamp ? { timestamp } : {}),
        }
    }

    const parts = contentParts(content)
    if (parts.length > 0) {
        return {
            purpose: "human",
            kind: "message",
            parts,
            ...(timestamp ? { timestamp } : {}),
        }
    }

    return {
        purpose: "unknown",
        kind: "message",
        parts: [{ type: "unknown", raw: content }],
        ...(timestamp ? { timestamp } : {}),
    }
}

function messageFromRecord(
    record: Record<string, unknown>,
    purpose: ProviderUserMessagePurpose,
    timestamp?: string,
    options?: { omitCodexImageWrappers?: boolean; providerTurnId?: string }
): ProviderUserMessage | null {
    const content = contentParts(record.content, {
        omitCodexImageWrappers: options?.omitCodexImageWrappers,
    })
    const parts = content.length > 0 ? content : textParts(stringValue(record.message) ?? stringValue(record.text) ?? stringValue(record.input))
    if (parts.length === 0) return null
    return {
        purpose,
        kind: "message",
        parts,
        ...(timestamp ? { timestamp } : {}),
        ...(options?.providerTurnId ? { providerTurnId: options.providerTurnId } : {}),
    }
}

function contentParts(content: unknown, options?: { omitCodexImageWrappers?: boolean }): ProviderContentPart[] {
    if (typeof content === "string") return textParts(content)
    if (!Array.isArray(content)) return []
    const parts: ProviderContentPart[] = []
    for (const block of content) {
        if (typeof block === "string") {
            const text = block.trim()
            if (text) parts.push({ type: "text", text })
            continue
        }
        const record = recordValue(block)
        if (!record) continue
        const type = stringValue(record.type)
        if (type === "text" || type === "input_text" || type === "output_text") {
            const text = stringValue(record.text)?.trim()
            if (text && !(options?.omitCodexImageWrappers && isCodexImageWrapper(text))) parts.push({ type: "text", text })
            continue
        }
        if (type === "image" || type === "image_url" || type === "input_image") {
            const image = imagePart(record)
            if (image) {
                parts.push(image)
                continue
            }
        }
        if (type && type !== "tool_result") parts.push({ type: "unknown", raw: block })
    }
    return parts
}

function textParts(text: string | undefined): ProviderContentPart[] {
    const trimmed = text?.trim()
    return trimmed ? [{ type: "text", text: trimmed }] : []
}

function containsToolResult(content: unknown): boolean {
    if (!Array.isArray(content)) return false
    return content.some((block) => stringValue(recordValue(block)?.type) === "tool_result")
}

function imagePart(record: Record<string, unknown>): Extract<ProviderContentPart, { type: "image" }> | null {
    const directBase64 = stringValue(record.data)
    let directMediaType = stringValue(record.media_type)
    if (!directMediaType) directMediaType = stringValue(record.mimeType)
    if (directBase64 && directMediaType) {
        return {
            type: "image",
            uri: `data:${directMediaType};base64,${directBase64}`,
            mediaType: directMediaType,
        }
    }
    const source = recordValue(record.source)
    const mediaType = stringValue(record.media_type) ?? stringValue(record.mediaType) ?? stringValue(source?.media_type) ?? stringValue(source?.mediaType)
    const direct = stringValue(record.uri) ?? stringValue(record.url) ?? stringValue(record.image_url)
    const imageUrl = recordValue(record.image_url)
    const uri = direct ?? stringValue(imageUrl?.url) ?? stringValue(source?.uri) ?? stringValue(source?.url)
    if (uri) {
        const resolvedMediaType = mediaTypeFromDataURL(uri) ?? mediaType
        return {
            type: "image",
            uri,
            ...(resolvedMediaType ? { mediaType: resolvedMediaType } : {}),
        }
    }

    const base64 = stringValue(source?.data)
    if (stringValue(source?.type) === "base64" && base64 && mediaType)
        return {
            type: "image",
            uri: `data:${mediaType};base64,${base64}`,
            mediaType,
        }
    return null
}

function mediaTypeFromDataURL(uri: string): string | undefined {
    return /^data:([^;,]+)[;,]/i.exec(uri)?.[1]
}

function isCodexImageWrapper(text: string): boolean {
    return text === "</image>" || /^<image\s+name=\[Image #\d+\]\s+path="[^"]+">$/.test(text)
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined
}
